/**
 * Custom event for coordinating audio playback across all voiceover components.
 * When any audio source starts playing, it dispatches this event so other
 * audio sources can pause themselves.
 *
 * Used by:
 * - usePlayAudio hook (VoiceCard, ContributorVoiceCard, TextInputPlayButton)
 * - GeneratedVoicePlayer
 */
export class AudioPlayEvent extends Event {
    public readonly src: string;
    public readonly instanceId: string;
    static readonly type = 'audio:play';

    constructor(src: string, instanceId: string) {
        super(AudioPlayEvent.type);
        this.src = src;
        this.instanceId = instanceId;
    }
}

/**
 * Dispatch an AudioPlayEvent to notify all audio sources that playback has started.
 * Includes SSR guard to prevent errors during server-side rendering.
 *
 * @param src - The audio URL being played
 * @param instanceId - Unique identifier for the audio player instance
 */
export function dispatchAudioPlay(src: string, instanceId: string): void {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new AudioPlayEvent(src, instanceId));
    }
}

/**
 * Generate a unique instance ID for audio coordination.
 */
export function generateAudioInstanceId(): string {
    return `audio-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
